/**
 * Users Page
 * Displays list of users with navigation sidebar for role filtering
 * Uses shared layout system like ContentPage
 */

import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import {
    Search,
    Plus,
    Trash2,
    UserCog,
    RefreshCw,
    Loader2,
    Info,
    LayoutGrid,
    ChevronLeft,
    ChevronRight,
    Filter as FilterIcon,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterBuilder, type Filter } from '@/components/filters';
import { useUsers, useDeleteUsers } from '@/hooks/useUsers';
import { useRoles } from '@/hooks/access-control';
import { getUserDisplayName, type User } from '@/api/users';
import { UsersNavigation } from '@/components/users/UsersNavigation';
import { usePreset } from '@/hooks/usePreset';
import { useRightSidebar, type SidebarSection } from '@/components/sidebar/index';
import { LayoutWrapper, LayoutOptionsWrapper, getAvailableLayouts, type LayoutId } from '@/layouts';
import { toast } from 'sonner';

// Users collection name
const USERS_COLLECTION = 'cms_users';

// User fields for layout display
const USER_FIELDS = [
    { field: 'id', type: 'uuid', meta: { hidden: true }, schema: { is_primary_key: true } },
    { field: 'first_name', type: 'string', meta: { note: 'First Name' }, schema: {} },
    { field: 'last_name', type: 'string', meta: { note: 'Last Name' }, schema: {} },
    { field: 'email', type: 'string', meta: { note: 'Email' }, schema: {} },
    { field: 'avatar', type: 'uuid', meta: { note: 'Avatar', special: ['file'] }, schema: {} },
    { field: 'title', type: 'string', meta: { note: 'Title' }, schema: {} },
    { field: 'status', type: 'string', meta: { note: 'Status' }, schema: {} },
    { field: 'role', type: 'uuid', meta: { note: 'Role', special: ['m2o'] }, schema: {} },
];

export function UsersPage() {
    const navigate = useNavigate();
    const { roleId } = useParams<{ roleId?: string }>();

    // Use preset for users collection - defaults to cards layout
    const {
        layout,
        setLayout,
        layoutQuery,
        setLayoutQuery,
        layoutOptions,
        allLayoutOptions,
        setLayoutOptions,
        filter: presetFilter,
        setFilter: setPresetFilter,
        search: presetSearch,
        setSearch: setPresetSearch,
    } = usePreset({
        collection: USERS_COLLECTION,
    });

    // Current layout - default to cards for users
    const currentLayout = (layout || 'cards') as LayoutId;

    // State
    const [searchInput, setSearchInput] = useState('');
    const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

    // Handle filter change from FilterBuilder
    const handleFilterChange = (filter: Filter | null) => {
        setPresetFilter(filter as Record<string, unknown> | null);
    };

    // Count active filters
    const activeFilterCount = useMemo(() => {
        if (!presetFilter) return 0;
        const filter = presetFilter as Filter | null;
        if (filter?._and && Array.isArray(filter._and)) {
            return filter._and.length;
        }
        if (filter?._or && Array.isArray(filter._or)) {
            return filter._or.length;
        }
        return 0;
    }, [presetFilter]);

    // Build merged filter including role filter if selected
    const mergedFilter = useMemo(() => {
        const filters: Record<string, unknown>[] = [];

        // Add role filter if role is selected
        if (roleId) {
            filters.push({ role: { _eq: roleId } });
        }

        // Add preset filter if exists
        if (presetFilter && typeof presetFilter === 'object') {
            filters.push(presetFilter as Record<string, unknown>);
        }

        if (filters.length === 0) return undefined;
        if (filters.length === 1) return filters[0];
        return { _and: filters };
    }, [roleId, presetFilter]);

    // Query - always use useUsers with combined filter
    const limit = layoutQuery?.limit || 25;
    const page = layoutQuery?.page || 1;
    const offset = (page - 1) * limit;

    const usersQuery = useUsers({
        fields: ['*'],
        search: presetSearch || searchInput || undefined,
        filter: mergedFilter,
        sort: layoutQuery?.sort,
        limit,
        offset,
    });

    const { data: roles = [] } = useRoles();
    const deleteUsersMutation = useDeleteUsers();

    // Get current role name for header
    const currentRole = useMemo(() => {
        if (!roleId) return null;
        return roles.find(r => r.id === roleId);
    }, [roleId, roles]);

    // Build role map for user list items
    const roleMap = useMemo(() => {
        const map = new Map<string, string>();
        roles.forEach(role => map.set(role.id, role.name));
        return map;
    }, [roles]);

    // Users list
    const users: User[] = usersQuery.data?.data || [];
    const meta = usersQuery.data?.meta;

    // Map users to layout items
    const items = useMemo(() => {
        return users.map(user => ({
            ...user,
            // Add display name for card title
            _display_name: getUserDisplayName(user),
            // Add role name
            _role_name: user.role ? roleMap.get(typeof user.role === 'string' ? user.role : user.role.id) : undefined,
        }));
    }, [users, roleMap]);

    // Pagination
    const totalPages = meta?.filter_count && (layoutQuery?.limit || 25)
        ? Math.ceil(meta.filter_count / (layoutQuery?.limit || 25))
        : 1;
    const currentPage = layoutQuery?.page || 1;

    // Handlers
    const handleSearch = () => {
        setPresetSearch(searchInput || null);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleCreateUser = () => {
        navigate(roleId ? `/admin/users/roles/${roleId}/+` : '/admin/users/new');
    };

    const handleEditUser = (id: string | number) => {
        navigate(`/admin/users/${id}`);
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) return;
        try {
            await deleteUsersMutation.mutateAsync(selectedIds as string[]);
            setSelectedIds([]);
            setShowDeleteDialog(false);
            toast.success(`${selectedIds.length} user(s) deleted`);
        } catch {
            toast.error('Failed to delete users');
        }
    };

    // Visible fields for layouts
    const visibleFields = useMemo(() => {
        const fieldNames = layoutQuery?.fields || ['first_name', 'last_name', 'email', 'status'];
        return fieldNames.map(name => USER_FIELDS.find(f => f.field === name)).filter(Boolean) as typeof USER_FIELDS;
    }, [layoutQuery?.fields]);

    // Register sidebar sections - only show layout/export when there are records
    const hasRecords = items.length > 0;

    const sidebarSections = useMemo<SidebarSection[]>(() => {
        const sections: SidebarSection[] = [
            {
                id: `info-users-${roleId || 'all'}`,
                icon: <Info className="h-5 w-5" />,
                title: 'Information',
                defaultOpen: true,
                content: (
                    <div className="space-y-4 text-sm">
                        <div>
                            <span className="text-muted-foreground">Module</span>
                            <p className="font-medium">User Directory</p>
                        </div>
                        {currentRole && (
                            <div>
                                <span className="text-muted-foreground">Role</span>
                                <p className="font-medium">{currentRole.name}</p>
                            </div>
                        )}
                        {meta?.filter_count !== undefined && (
                            <div>
                                <span className="text-muted-foreground">Users</span>
                                <p className="font-medium">{meta.filter_count.toLocaleString()}</p>
                            </div>
                        )}
                    </div>
                ),
            },
        ];

        // Only show layout/export sections when there are records
        if (hasRecords) {
            sections.push({
                id: `layout-users-${currentLayout}-${roleId || 'all'}`,
                icon: <LayoutGrid className="h-5 w-5" />,
                title: 'Layout Options',
                content: (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Layout</Label>
                            <Select value={currentLayout} onValueChange={(v) => setLayout(v)}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select layout" />
                                </SelectTrigger>
                                <SelectContent>
                                    {getAvailableLayouts().map((layoutConfig) => (
                                        <SelectItem key={layoutConfig.id} value={layoutConfig.id}>
                                            {layoutConfig.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Layout-specific options */}
                        <LayoutOptionsWrapper
                            layout={currentLayout}
                            layoutOptions={allLayoutOptions as any || {}}
                            onLayoutOptionsChange={(opts) => setLayoutOptions((opts as any)?.[currentLayout] || null)}
                            collection={USERS_COLLECTION}
                            fields={USER_FIELDS as any}
                        />
                    </div>
                ),
            });
        }

        return sections;
    }, [roleId, currentRole, meta?.filter_count, currentLayout, hasRecords, setLayout, layoutOptions, setLayoutOptions]);

    // Register sidebar content
    useRightSidebar(sidebarSections);

    // Loading state
    const isLoading = usersQuery.isLoading;

    return (
        <div className="flex h-full">
            {/* Navigation Sidebar */}
            <UsersNavigation />

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <div className="border-b border-border bg-background px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                <UserCog className="h-5 w-5" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold text-foreground">
                                    {currentRole ? currentRole.name : 'User Directory'}
                                </h1>
                                <p className="text-sm text-muted-foreground mt-0.5">
                                    {isLoading
                                        ? 'Loading...'
                                        : `${meta?.filter_count || items.length} user${(meta?.filter_count || items.length) !== 1 ? 's' : ''}`}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Search with filter */}
                            <div className="relative flex items-center">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    type="search"
                                    placeholder="Search users..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    className="pl-9 pr-10 w-64"
                                />
                                {/* Filter button inside search */}
                                <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                "absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7",
                                                activeFilterCount > 0 && "text-primary"
                                            )}
                                        >
                                            <FilterIcon className="h-4 w-4" />
                                            {activeFilterCount > 0 && (
                                                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                                                    {activeFilterCount}
                                                </span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[500px]" align="end">
                                        <div className="space-y-4">
                                            <h4 className="font-medium">Filters</h4>
                                            <FilterBuilder
                                                value={presetFilter as Filter | null}
                                                onChange={handleFilterChange}
                                                fields={USER_FIELDS as any}
                                                collection={USERS_COLLECTION}
                                            />
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Selection actions */}
                            {selectedIds.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setShowDeleteDialog(true)}
                                    className="text-destructive hover:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}

                            {/* Add user button */}
                            <Button onClick={handleCreateUser}>
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Secondary toolbar */}
                <div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-2">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {selectedIds.length > 0 ? (
                            <span>{selectedIds.length} user{selectedIds.length > 1 ? 's' : ''} selected</span>
                        ) : (
                            <span>
                                {meta?.filter_count !== undefined ? `${meta.filter_count} users` : 'Loading...'}
                            </span>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => usersQuery.refetch()}
                            disabled={usersQuery.isFetching}
                        >
                            <RefreshCw className={cn('h-4 w-4', usersQuery.isFetching && 'animate-spin')} />
                        </Button>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <Select
                            value={String(layoutQuery?.limit || 25)}
                            onValueChange={(value) => setLayoutQuery({ ...layoutQuery, limit: Number(value), page: 1 })}
                        >
                            <SelectTrigger className="h-8 w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Main content area */}
                <div className="flex-1 flex overflow-hidden">
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-auto">
                            {isLoading ? (
                                <div className="flex h-full items-center justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : items.length === 0 ? (
                                <UsersEmptyState
                                    hasSearch={!!searchInput || !!presetSearch}
                                    roleId={roleId}
                                    onClearSearch={() => {
                                        setSearchInput('');
                                        setPresetSearch(null);
                                    }}
                                />
                            ) : (
                                <LayoutWrapper
                                    layout={currentLayout}
                                    collection={USERS_COLLECTION}
                                    primaryKeyField="id"
                                    items={items}
                                    loading={isLoading}
                                    error={null}
                                    page={currentPage}
                                    limit={layoutQuery?.limit || 25}
                                    totalCount={meta?.filter_count}
                                    itemCount={items.length}
                                    totalPages={totalPages}
                                    onPageChange={(page) => setLayoutQuery({ ...layoutQuery, page })}
                                    onLimitChange={(limit) => setLayoutQuery({ ...layoutQuery, limit, page: 1 })}
                                    selection={selectedIds}
                                    onSelectionChange={setSelectedIds}
                                    showSelect="multiple"
                                    sort={layoutQuery?.sort || []}
                                    onSortChange={(sort) => setLayoutQuery({ ...layoutQuery, sort })}
                                    layoutOptions={layoutOptions as any || {}}
                                    onLayoutOptionsChange={(opts) => setLayoutOptions(opts as any)}
                                    layoutQuery={(layoutQuery as any) || {}}
                                    onLayoutQueryChange={(q) => setLayoutQuery(q as any)}
                                    fields={visibleFields as any}
                                    onItemClick={(item) => handleEditUser(item.id as string)}
                                    onRefresh={() => usersQuery.refetch()}
                                    readonly={false}
                                />
                            )}
                        </div>

                        {/* Footer / Pagination */}
                        {items.length > 0 && (
                            <div className="flex items-center justify-between border-t border-border bg-background px-6 py-3">
                                <div className="text-sm text-muted-foreground">
                                    {meta?.filter_count !== undefined && (
                                        <>
                                            Showing {((currentPage - 1) * (layoutQuery?.limit || 25)) + 1} to{' '}
                                            {Math.min(currentPage * (layoutQuery?.limit || 25), meta.filter_count)} of{' '}
                                            {meta.filter_count} users
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setLayoutQuery({ ...layoutQuery, page: currentPage - 1 })}
                                        disabled={currentPage <= 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setLayoutQuery({ ...layoutQuery, page: currentPage + 1 })}
                                        disabled={currentPage >= totalPages}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Users</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''}?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteSelected}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteUsersMutation.isPending ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// Empty state
interface UsersEmptyStateProps {
    hasSearch: boolean;
    roleId?: string;
    onClearSearch: () => void;
}

function UsersEmptyState({ hasSearch, roleId, onClearSearch }: UsersEmptyStateProps) {
    return (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
            <div className="rounded-full bg-muted p-4 mb-4">
                <UserCog className="h-8 w-8 text-muted-foreground" />
            </div>

            {hasSearch ? (
                <>
                    <h3 className="text-lg font-medium text-foreground">No users found</h3>
                    <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                        No users match your search. Try a different search term.
                    </p>
                    <Button variant="outline" className="mt-4" onClick={onClearSearch}>
                        Clear Search
                    </Button>
                </>
            ) : (
                <>
                    <h3 className="text-lg font-medium text-foreground">
                        {roleId ? 'No users in this role' : 'No users yet'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm">
                        {roleId
                            ? 'There are no users assigned to this role.'
                            : 'Get started by inviting your first users.'}
                    </p>

                </>
            )}
        </div>
    );
}
